import * as React from 'react';
import { readableTileTone } from './readable-tile-tone';
import css from './module-launcher.module.css';

const s = css as any;

/**
 * A module whose code is a short all-capitals acronym (CCM, PCM, RPM, RTM) is shown as its
 * letters: no picture tells RPM from RTM, and staff say the letters anyway. The rule reads the
 * text, so a new acronym module needs no code change.
 */
export const moduleLetters = (code?: string): string | undefined => {
  const text = ( code || '' ).trim();
  return /^[A-Z]{2,4}$/.test(text) ? text : undefined;
};

interface IModuleIconProps {
  /** The module's icon class (Font Awesome). */
  icon?: string;
  /** A ready glyph (e.g. an antd icon) for apps without Font Awesome; drawn instead of `icon`. */
  glyph?: React.ReactNode;
  /** The module's stored color; clamped so white always reads. */
  color?: string;
  /** Text that, when an all-capitals acronym, is drawn as letters instead of the icon. */
  code?: string;
  size?: 'lg' | 'sm';
  className?: string;
}

/**
 * A module's app icon: one rounded square filled from its color, with a white glyph or the
 * acronym. Shared by every app's Modules drawer and by Merchant.Web's Arrange editor.
 */
export const ModuleIcon: React.FC<IModuleIconProps> = ({ icon, glyph, color, code, size = 'lg', className }) => {
  const letters = moduleLetters(code);
  return (
    <span
      className={`${s.ModuleIcon} ${size === 'sm' ? s.ModuleIconSm : ''} ${className || ''}`}
      style={{ '--module-tone': readableTileTone(color) } as React.CSSProperties}
      aria-hidden
    >
      {letters
        ? <b className={s.ModuleIconLetters}>{letters}</b>
        : glyph
          ? <span className={s.ModuleIconGlyph}>{glyph}</span>
          : <i className={icon}/>}
    </span>
  );
};
